import { Component, computed, EventEmitter, input, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import {
    CompanyProductSelectionDTO,
    CompanyProductSelectionPricingOptionWithOverrideInformation,
    PeriodType,
    PricingOptionType,
    ProductPricingOptionDTO
} from '@iota/iota-management-portal-v4-client';
import { getNumberFormattedWithDecimalPlaces, isEmptyOrNull } from 'src/app/shared/utils';
import * as _ from 'lodash';
import { PricingOptionTypeDescriptions } from 'src/app/shared/constants-and-enums/pricing-option-type';
import { FormBuilder, FormControl, Validators } from '@angular/forms';
import { KeysOfType } from 'src/app/shared/constants-and-enums/helper-types';
import { TaxCategory, taxCategoriesWithLabels } from 'src/app/shared/constants-and-enums/tax-category';
import { AsyncState } from 'src/app/shared/utils/async-state.util';
import { PriceScaleFormValue } from 'src/app/shared/utils/price-scale-intervals.util';
import { getValueType, PricingOptionValueType } from 'src/app/shared/utils/pricing-option-value-type.util';

// Type-safe form (includes UI-only fields taxCategory and taxRate)
type PricingOptionFormKeys = keyof ProductPricingOptionDTO | 'taxCategory' | 'taxRate';
type PricingOptionControls = Partial<Record<PricingOptionFormKeys, FormControl>>;

const PROPERTIES_TO_FORMAT_WITH_AT_LEAST_TWO_DECIMAL_PLACES: KeysOfType<ProductPricingOptionDTO, number>[] = [
    'optionPricing',
    'productOptionCostsMonthly',
    'productOptionCostsYearly'
];

@Component({
    standalone: false,
    selector: 'app-product-pricing-option',
    templateUrl: './product-pricing-option.component.html',
    styleUrls: ['./product-pricing-option.component.scss']
})
export class ProductPricingOptionComponent implements OnInit, OnChanges {
    readonly valueTypes = PricingOptionValueType;

    valueType: PricingOptionValueType = PricingOptionValueType.NUMBER;
    editMode = false;

    taxCategoryOptions = taxCategoriesWithLabels;

    pricingOptionForm = this.fb.group<PricingOptionControls>({
        optionName: new FormControl('', { validators: [Validators.required] }),
        reportingName: new FormControl(''),
        customerReportingName: new FormControl(''),
        recommended: new FormControl(false),
        optionKey: new FormControl('', { validators: [Validators.required] }),
        productOptionInternalNameKey: new FormControl(''),
        optionType: new FormControl(PricingOptionType.FIXED, { validators: [Validators.required] }),
        optionNumberValue: new FormControl<number>(null),
        optionDateValue: new FormControl<Date>(null),
        optionTextValue: new FormControl<string>(null),
        optionPricing: new FormControl<number>(null),
        optionPricingPeriod: new FormControl<PeriodType>(null),
        productOptionCostsMonthly: new FormControl<number>(null),
        productOptionCostsYearly: new FormControl<number>(null),
        externalPricingApproval: new FormControl<boolean>(false),
        taxCategory: new FormControl<TaxCategory>(TaxCategory.UNKNOWN),
        taxRate: new FormControl<number>(null, { validators: [Validators.min(0), Validators.max(1)] }),
        useProductPriceScale: new FormControl<boolean>(false)
    });

    readonly priceScaleState = input.required<AsyncState<PriceScaleFormValue | null>>();

    readonly priceScale = computed(() => {
        const state = this.priceScaleState();
        return state.status === 'ok' ? state.data : undefined;
    });
    readonly priceScaleLoading = computed(() => this.priceScaleState().status === 'loading');
    readonly priceScaleError = computed(() => this.priceScaleState().status === 'error');
    readonly priceScaleMissing = computed(() => this.priceScale() === null);

    isOverride = false;
    isOverwritten = false;

    @Input() pricingOption: ProductPricingOptionDTO | CompanyProductSelectionPricingOptionWithOverrideInformation;
    @Input() allPricingOptions: ProductPricingOptionDTO[];
    @Input() companyProductSelection: CompanyProductSelectionDTO;
    @Input() editable = false;
    @Input() duplicable = false;

    @Output() save: EventEmitter<ProductPricingOptionDTO> = new EventEmitter<ProductPricingOptionDTO>();
    @Output() delete: EventEmitter<ProductPricingOptionDTO> = new EventEmitter<ProductPricingOptionDTO>();
    @Output() cancelEdit: EventEmitter<ProductPricingOptionDTO> = new EventEmitter<ProductPricingOptionDTO>();
    @Output() duplicate: EventEmitter<ProductPricingOptionDTO | CompanyProductSelectionPricingOptionWithOverrideInformation> =
        new EventEmitter<ProductPricingOptionDTO | CompanyProductSelectionPricingOptionWithOverrideInformation>();

    optionPricingPeriodOptions = [
        { value: PeriodType.YEARLY, label: $localize`Year` },
        { value: PeriodType.MONTHLY, label: $localize`Month` },
        { value: PeriodType.DAILY, label: $localize`Day` }
    ];

    constructor(private fb: FormBuilder) {}

    ngOnInit(): void {
        this.initForm(isEmptyOrNull(this.pricingOption.productPricingOptionId));
    }

    ngOnChanges(changes: SimpleChanges): void {
        const prevPricingOption = changes?.pricingOption?.previousValue;
        const currentPricingOption = changes?.pricingOption?.currentValue;
        const pricingOptionChanged = !_.isEqual(prevPricingOption, currentPricingOption);

        if (pricingOptionChanged) {
            this.initForm(isEmptyOrNull(this.pricingOption.productPricingOptionId));
        }
    }

    initForm(enableEditing = false) {
        this.valueType = getValueType(this.pricingOption);

        if (this.isPricingOptionWithOverrideInformation(this.pricingOption)) {
            this.isOverwritten = this.pricingOption?.overwritten;
            this.isOverride = this.pricingOption?.override;
        }

        this.pricingOptionForm.patchValue({
            optionName: this.pricingOption?.optionName,
            reportingName: this.pricingOption?.reportingName,
            customerReportingName: this.pricingOption?.customerReportingName,
            recommended: this.pricingOption?.recommended,
            optionKey: this.pricingOption?.optionKey,
            productOptionInternalNameKey: this.pricingOption?.productOptionInternalNameKey,
            optionType: this.pricingOption?.optionType ?? PricingOptionType.FIXED,
            optionNumberValue: this.pricingOption?.optionNumberValue,
            optionDateValue: this.pricingOption?.optionDateValue,
            optionTextValue: this.pricingOption?.optionTextValue,
            optionPricing: this.pricingOption?.optionPricing,
            optionPricingPeriod: this.pricingOption?.optionPricingPeriod as PeriodType,
            productOptionCostsMonthly: this.pricingOption?.productOptionCostsMonthly,
            productOptionCostsYearly: this.pricingOption?.productOptionCostsYearly,
            externalPricingApproval: this.pricingOption?.externalPricingApproval ?? false,
            useProductPriceScale: this.pricingOption?.useProductPriceScale ?? false
        });

        this.initAndPatchTaxCategoryAndRate();

        if (enableEditing) {
            this.editMode = true;
            this.pricingOptionForm.enable();
        } else {
            this.editMode = false;
            this.pricingOptionForm.disable();

            // Numbers are formatted as long as the form is disabled
            for (const property of PROPERTIES_TO_FORMAT_WITH_AT_LEAST_TWO_DECIMAL_PLACES) {
                this.pricingOptionForm.get(property)?.setValue(getNumberFormattedWithDecimalPlaces(this.pricingOption?.[property]));
            }
            const taxRateControl = this.pricingOptionForm.get('taxRate');
            if (taxRateControl?.value != null) {
                taxRateControl.setValue(getNumberFormattedWithDecimalPlaces(taxRateControl.value));
            }
        }
    }

    initAndPatchTaxCategoryAndRate() {
        const insuranceTax = this.pricingOption?.productCostsInsuranceTax;
        const salesTax = this.pricingOption?.productCostsSalesTax;
        const passThrough = this.pricingOption?.productCostsSalesTaxPassThrough;

        const hasInsuranceTax = insuranceTax !== null && insuranceTax !== undefined;
        const hasSalesTax = salesTax !== null && salesTax !== undefined;

        if ((hasInsuranceTax && hasSalesTax) || (!hasInsuranceTax && !hasSalesTax)) {
            this.pricingOptionForm.patchValue({ taxCategory: TaxCategory.UNKNOWN, taxRate: insuranceTax ?? salesTax ?? null });
            return;
        }

        if (hasInsuranceTax) {
            this.pricingOptionForm.patchValue({ taxCategory: TaxCategory.INSURANCE_TAX, taxRate: insuranceTax });
            return;
        }

        this.pricingOptionForm.patchValue({
            taxCategory: passThrough ? TaxCategory.SALES_TAX_PASS_THROUGH : TaxCategory.SALES_TAX,
            taxRate: salesTax
        });
    }

    get useProductPriceScale(): boolean {
        return !!this.pricingOptionForm.get('useProductPriceScale')?.value;
    }

    isPricingOptionWithOverrideInformation(
        pricingOption: typeof this.pricingOption
    ): pricingOption is CompanyProductSelectionPricingOptionWithOverrideInformation {
        if (!pricingOption) return false;
        return 'overwritten' in pricingOption;
    }

    enableEditing() {
        this.initForm(true);
    }

    disableEditingAndDontInitializeForm() {
        this.editMode = false;
        this.pricingOptionForm.disable();
    }

    getOptionTypeDescription(optionType: string) {
        return PricingOptionTypeDescriptions.get(optionType);
    }

    resetOptionValue() {
        this.pricingOptionForm.get('optionDateValue').reset();
        this.pricingOptionForm.get('optionNumberValue').reset();
        this.pricingOptionForm.get('optionTextValue').reset();
    }

    savePricingOption() {
        if (this.pricingOptionForm.valid) {
            const previousPricingOption = _.cloneDeep(this.pricingOption);

            // Convert tax category + rate back to the two separate DTO fields
            const taxCategory = this.pricingOptionForm.get('taxCategory')?.value as TaxCategory;
            const taxRate = this.pricingOptionForm.get('taxRate')?.value as number;
            let productCostsInsuranceTax: number = null;
            let productCostsSalesTax: number = null;
            let productCostsSalesTaxPassThrough = false;

            switch (taxCategory) {
                case TaxCategory.INSURANCE_TAX:
                    productCostsInsuranceTax = taxRate;
                    break;
                case TaxCategory.SALES_TAX:
                    productCostsSalesTax = taxRate;
                    break;
                case TaxCategory.SALES_TAX_PASS_THROUGH:
                    productCostsSalesTax = taxRate;
                    productCostsSalesTaxPassThrough = true;
                    break;
                case TaxCategory.UNKNOWN:
                    productCostsInsuranceTax = null;
                    productCostsSalesTax = null;
                    productCostsSalesTaxPassThrough = false;
                    break;
            }

            const updatedPricingOption: ProductPricingOptionDTO = {
                ...previousPricingOption,
                ...this.pricingOptionForm.getRawValue(),
                productCostsInsuranceTax,
                productCostsSalesTax,
                productCostsSalesTaxPassThrough
            };

            this.save.emit(updatedPricingOption);
            this.disableEditingAndDontInitializeForm();
        }
    }

    deletePricingOption() {
        this.delete.emit(this.pricingOption);
    }

    duplicatePricingOption() {
        this.duplicate.emit(this.pricingOption);
    }

    onCancel() {
        this.initForm(false);
        this.cancelEdit.emit();
    }
}
